import type {
  FixedRateLoanDealProductBuilderRow,
  FloatingRateLoanDealProductBuilderRow,
} from '@src/types/OTC/otcProductBuilderTypes';
import {
  Box,
  ButtonVariants,
  FormControlSizes,
  LoanSecurityTypeEnum,
  ToggleGroup,
  type Column,
  type ToggleGroupOption,
} from '@talos/kyoko';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';

type LoanProductBuilderRow = FixedRateLoanDealProductBuilderRow | FloatingRateLoanDealProductBuilderRow;

const LOAN_SECURITY_TYPE_OPTIONS: ToggleGroupOption<LoanSecurityTypeEnum>[] = [
  { value: LoanSecurityTypeEnum.Secured, displayName: 'Secured' },
  { value: LoanSecurityTypeEnum.Unsecured, displayName: 'Unsecured' },
];

interface BuildLoanSecurityTypeToggleColumnParams {
  headerName: string;
  description?: string;
  testIdPrefix?: string;
}

/**
 * Inline Secured/Unsecured toggle for the product-builder grid.
 * Uses setDataValue so readOnlyEdit + onCellEditRequest updates the row.
 */
export function buildLoanSecurityTypeToggleColumn({
  headerName,
  description,
  testIdPrefix = 'loan-security-type',
}: BuildLoanSecurityTypeToggleColumnParams): Column<ColDef<LoanProductBuilderRow>, LoanProductBuilderRow> {
  return {
    field: 'loanSecurityType',
    type: 'custom',
    title: headerName,
    description,
    editable: false,
    // Match trailing cell space of the adjacent Select (currency) columns: content + a bit of end padding.
    minWidth: 156,
    width: 156,
    params: {
      valueGetter: ({ data }) => data?.loanSecurityType ?? LoanSecurityTypeEnum.Secured,
      cellRenderer: ({ data, node }: ICellRendererParams<LoanProductBuilderRow>) => {
        if (!data) {
          return null;
        }
        const value = data.loanSecurityType ?? LoanSecurityTypeEnum.Secured;
        return (
          <Box w="100%" minWidth={0}>
            <ToggleGroup<LoanSecurityTypeEnum>
              options={LOAN_SECURITY_TYPE_OPTIONS}
              value={value}
              size={FormControlSizes.Small}
              orientation="horizontal"
              selectionAnimation="slide"
              equalWidth
              defaultSelectedVariant={ButtonVariants.Priority}
              data-testid={`${testIdPrefix}-toggle`}
              onChange={(nextValue: LoanSecurityTypeEnum) => {
                node.setDataValue('loanSecurityType', nextValue);
              }}
            />
          </Box>
        );
      },
    },
  };
}
